import { Box, Button, Chip, SxProps } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { IUser, useAddRoleToUserMutation, useGetUsersQuery } from '../../generated/graphql';
import { addUsersToGroupInCache } from '../../utils/clientUtils';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import DialogBox from '../Common/Layout/DialogBox';

type IProps = {
  id: string;
  name: string;
  values: IUser[];
  handleClose: () => void;
};

type NewUser = IUser & {
  isNew?: boolean;
};

const chipStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#69AAF333',
  '&:active, &:focus': {
    background: '#69AAF333'
  }
};

const newChipStyles: SxProps = {
  color: Colors.skyBlue,
  background: Colors.white,
  fontSize: 14,
  border: '1px solid ' + Colors.skyBlue,
  '&:active, &:focus': {
    background: Colors.white
  }
};

const deleteIconStyles: SxProps = {
  color: Colors.skyBlue,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

type IErrors = {
  chooseUser: string | null;
};

export const ErrorTexts = {
  chooseUser: 'Please select user!'
};

const defaultErrorObj: IErrors = {
  chooseUser: null
};

const AddUserToGroup: React.FunctionComponent<IProps> = ({
  id,
  name,
  values,
  handleClose
}: IProps) => {
  const { data } = useGetUsersQuery();
  const { dispatch } = useContext(AppContext);
  const [addRoleToUsers, { loading }] = useAddRoleToUserMutation();
  const [currentValues, setCurrentValues] = useState(values as NewUser[]);
  const [addedUsers, setAddedUsers] = useState<IUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [usersOptions, setUsersOptions] = useState<IUser[]>([]);
  const [formErrors, setFormErrors] = useState(defaultErrorObj);

  useEffect(() => {
    if (data && data.result) {
      setUsersOptions(data.result.filter((r) => !r?.roles.includes(name)) as never[]);
    }
  }, [data]);

  const handleUserChange = (_: unknown, newValue: IUser | null) => {
    if (newValue) {
      setFormErrors({ ...formErrors, chooseUser: null });
    } else {
      setFormErrors({ ...formErrors, chooseUser: ErrorTexts.chooseUser });
    }
    setSelectedUser(newValue);
  };

  const handleAddUserButtonPressed = () => {
    if (selectedUser) {
      setCurrentValues([{ ...selectedUser, isNew: true }, ...currentValues]);
      setAddedUsers([...addedUsers, selectedUser]);
      setSelectedUser(null);
      setUsersOptions(usersOptions.filter((u) => u.id !== selectedUser.id));
    }
  };

  const handleRemoveUser = (user: NewUser) => {
    setCurrentValues(currentValues.filter((v) => v.id !== user.id));
    setAddedUsers(addedUsers.filter((v) => v.id !== user.id));
    setUsersOptions([...usersOptions, user]);
  };

  const submitForm = () => {
    const errors = {
      chooseUser: null
    } as IErrors;
    if (addedUsers.length > 0) {
      addRoleToUsers({
        variables: {
          role_id: parseInt(id),
          user_ids: addedUsers.map((u) => parseInt(u.id))
        },
        update() {
          setCurrentValues(currentValues.map((v) => (v.isNew ? { ...v, isNew: false } : v)));
          setAddedUsers([]);
          addUsersToGroupInCache(id, addedUsers as IUser[]);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `User(s) added to ${name} group successfully`
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      errors.chooseUser = ErrorTexts.chooseUser;
      setFormErrors(errors);
    }
  };

  const handleCancelPressed = () => {
    if (addedUsers.length > 0) {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: 'There are unsaved changes. Are you sure you want to proceed?',
          agreeAction: closeDialog
        }
      });
    } else {
      handleClose();
    }
  };

  const closeDialog = () => {
    dispatch({
      type: Actions.HideConfirmation
    });
    handleClose();
  };
  return (
    <DialogBox id="new-user-dialog" width={886} title={'Add User'} onClose={handleClose}>
      <Box
        sx={{ height: '668px', display: 'flex', flexFlow: 'column', paddingY: '70px', gap: '50px' }}
      >
        <Box
          sx={{
            color: Colors.skyBlue,
            fontSize: '18px',
            lineHeight: '19px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}
        >
          {name.replace(/([A-Z][a-z])/g, ' $1').replace(/(\d)/g, ' $1')}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: '20px 24px',
            paddingLeft: '13%',
            paddingRight: '13%',
            flexGrow: 1,
            gridTemplateRows: 'max-content'
          }}
        >
          <AutocompleteComponent
            id="add-user-to-user-group-autocomplete"
            label="Choose User"
            values={selectedUser}
            options={usersOptions}
            onChange={handleUserChange}
            errorMsg={formErrors.chooseUser}
            disabled={loading}
            getOptionLabel={(option: IUser) => option?.username}
            additionalStyle={{
              gridColumn: 'span 8'
            }}
          />
          <Button
            id="add-user-to-user-group-button"
            data-testid="add-user-to-user-group-button"
            sx={{
              width: 126,
              height: '36px',
              border: '1px solid ' + Colors.secondary,
              color: Colors.secondary,
              gridColumn: 'span 3'
            }}
            onClick={handleAddUserButtonPressed}
            disabled={loading}
          >
            Add User
          </Button>
          <Box sx={{ gridColumn: 'span 12', display: 'flex', gap: '10px', marginTop: '50px' }}>
            {currentValues.map((val, index) => (
              <Box key={index}>
                {val.isNew ? (
                  <Chip
                    id={`${name}-${val.first_name} ${val.last_name}-val`}
                    data-testid={`${val.first_name} ${val.last_name}-${val}-val`}
                    label={`${val.first_name} ${val.last_name}`}
                    sx={{
                      ...newChipStyles,
                      '.MuiChip-deleteIcon': deleteIconStyles
                    }}
                    onDelete={() => handleRemoveUser(val)}
                  />
                ) : (
                  <Chip
                    id={`${name}-${val.first_name} ${val.last_name}-val`}
                    data-testid={`${val.first_name}-${val.last_name}-val`}
                    label={`${val.first_name} ${val.last_name}`}
                    sx={chipStyles}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="centralize">
          <CancelButton
            id="cancel-new-role-to-user-button"
            disabled={loading}
            onClick={handleCancelPressed}
            text={'Cancel'}
          />
          <SaveButton
            id="save-new-role-to-user-button"
            disabled={loading}
            onClick={submitForm}
            text={'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default AddUserToGroup;
